import React, { useState } from "react";
import HeaderImage from "../../components/HeaderImage";
import {
  PolicyElements,
  PolicyRequirements,
  PolicyTypes,
} from "../../utils/data.array";
import { useFetchPolicies } from "../../hooks/all.hooks";
import Checkbox from "../../components/Checkbox";
import { useNavigate } from "react-router-dom";
import PolicyForms from "../CallToAction/PolicyForm";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HoverLinkPreview from "../../components/HoverLinkPreview";
// import { FaFilter } from "react-icons/fa";
// import { motion } from "framer-motion";

type FilterType =
  | "policyType"
  | "policyElement"
  | "policyRequirement"
  | "country";

const PolicyObservatory: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const [selectedFilters, setSelectedFilters] = useState<{
    policyType: string[];
    policyElement: string[];
    policyRequirement: string[];
    country: string[];
    search: string;
  }>({
    policyType: [],
    policyElement: [],
    policyRequirement: [],
    country: [],
    search: "",
  });

  // const [isSidebarOpen] = useState(true);
  const [limit, setLimit] = useState(8);

  const {
    data,
    // isLoading,
    isError,
  } = useFetchPolicies({ ...selectedFilters, page: 1, limit }, (error: any) => {
    console.error("Error fetching policies:", error);
  });

  const policies = data?.policies || [];
  const pagination = data?.pagination;
  const availableCountries = data?.availableCountries || [];

  const handleCheckboxChange = (filterType: FilterType, value: string) => {
    setSelectedFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (updatedFilters[filterType].includes(value)) {
        updatedFilters[filterType] = updatedFilters[filterType].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterType] = [...updatedFilters[filterType], value];
      }
      return updatedFilters;
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };

  // const handleReadMore = (params: string) => {
  //   navigate(`/about-us/policy-observatory/${params}`);
  // };

  // const handleReadMore = (params: string) => {
  //   const slug = params.toLowerCase().replace(/\s+/g, "-");
  //   navigate(`/about-us/policy-observatory/${slug}`);
  // };

  const handleReadMore = (params: string) => {
    const slug = encodeURIComponent(params.toLowerCase().replace(/\s+/g, "-"));
    navigate(`/about-us/policy-observatory/${slug}`);
  };
  

  const loadMore = () => {
    setLimit((prev) => prev + 8);
  };

  // const toggleSidebar = () => {
  //   setIsSidebarOpen(!isSidebarOpen);
  // };

  return (
    <>
      <HeaderImage image="/assets/images/backgrounds/observatory.jpg" />

      <div className="container mx-auto px-4 mb-14" id="policy-observatory">
        <h2 className="text-center text-xxl font-semibold text-foreground uppercase my-4">
          Open Science Policy Observatory
        </h2>
        <p className="text-sm text-foreground mb-2">
          The Open Science Policy Observatory is a dedicated initiative aimed at
          tracking, analyzing, and promoting Open Science (OS) and Open Access
          (OA) policies across Africa. It provides insights into Institutional
          OS, National OS, Institutional OA, and National OA policies,
          supporting the development of a more open and collaborative research
          ecosystem.
        </p>

        <p className="text-sm text-foreground mb-2">
          The observatory highlights key policy elements, including open access
          repositories for publications and data, open access journals and
          publishing platforms, and other open science infrastructures. It also
          emphasizes the importance of recognizing OA/Open Science in research
          assessment and open data for government and the private sector.
        </p>

        <p className="text-sm text-foreground mb-8">
          To foster a robust Open Science culture, the observatory monitors
          policy requirements such as depositing in a repository upon
          publication, immediate or delayed open access under an open content
          license, publishing in OA journals, and making scientific outputs
          widely available. By advocating for policies that promote rights
          retention and preprint deposits, the observatory helps shape a
          transparent and accessible scientific landscape in Africa.
        </p>

        <div className="text-center mb-8">
          <button
            onClick={toggleModal}
            className="bg-primary hover:bg-primary-600 text-primary-foreground px-4 py-2"
          >
            Click to contribute to the Policy Observatory
          </button>
        </div>

        <div className="flex lg:space-x-24">
          <div className="hidden h-full max-h-screen max-w-[280px] min-w-[280px] flex-wrap overflow-auto rounded-sm bg-gray-50 pt-5 shadow-md sm:flex dark:bg-gray-900/70 dark:shadow-gray-800/40">
            <div className="px-6 py-4">
              <div className="mb-2">
                <input
                  type="text"
                  value={selectedFilters.search}
                  onChange={handleSearchChange}
                  placeholder="Search Policies..."
                  className="w-full p-2 mb-4 border rounded-md"
                />
              </div>
              <ul>
                <div className="mb-2">
                  <h3 className="mb-2 inline py-2 text-sm font-bold uppercase">
                    Country
                  </h3>
                  {availableCountries &&
                    availableCountries.map((t: any) => {
                      return (
                        <li key={t} className="my-1">
                          <Checkbox
                            key={t}
                            label={t}
                            check={selectedFilters.country.includes(t)}
                            setCheck={() => handleCheckboxChange("country", t)}
                          />
                        </li>
                      );
                    })}
                </div>

                <div className="mb-2">
                  <h3 className="mb-2 inline py-2 text-sm font-bold uppercase">
                    Policy Type
                  </h3>
                  {PolicyTypes.map((t: any) => {
                    return (
                      <li key={t} className="my-1">
                        <Checkbox
                          key={t.value}
                          label={t.label}
                          check={selectedFilters.policyType.includes(t.value)}
                          setCheck={() =>
                            handleCheckboxChange("policyType", t.value)
                          }
                        />
                      </li>
                    );
                  })}
                </div>

                <div className="mb-2">
                  <h3 className="mb-2 inline py-2 text-sm font-bold uppercase">
                    Policy Elements
                  </h3>
                  {PolicyElements.map((t: any) => {
                    return (
                      <li key={t} className="my-1">
                        <Checkbox
                          key={t.value}
                          label={t.label}
                          check={selectedFilters.policyElement.includes(
                            t.value
                          )}
                          setCheck={() =>
                            handleCheckboxChange("policyElement", t.value)
                          }
                        />
                      </li>
                    );
                  })}
                </div>

                <div className="mb-2">
                  <h3 className="mb-2 inline py-2 text-sm font-bold uppercase">
                    Policy Requirements
                  </h3>
                  {PolicyRequirements.map((t: any) => {
                    return (
                      <li key={t} className="my-1">
                        <Checkbox
                          key={t.value}
                          label={t.label}
                          check={selectedFilters.policyRequirement.includes(
                            t.value
                          )}
                          setCheck={() =>
                            handleCheckboxChange("policyRequirement", t.value)
                          }
                        />
                      </li>
                    );
                  })}
                </div>
              </ul>
            </div>
          </div>
          <div>
            {isError && (
              <div className="text-red-500">Failed to load policies.</div>
            )}
            {policies && policies.length > 0 ? (
              <ul>
                {policies.map((post: any, index: number) => {
                  const { policyTitle, policyDescription, organization } = post;
                  return (
                    <li key={index} className="py-5">
                      <article className="flex flex-col space-y-2 xl:space-y-0">
                        <dl>
                          <dt className="sr-only">Published Organization</dt>
                          <dd className="text-base leading-6 font-medium text-gray-500 dark:text-gray-400">
                            {(() => {
                            const slug = encodeURIComponent(policyTitle.toLowerCase().replace(/\s+/g, "-"));
                            const link = `/about-us/policy-observatory/${slug}`;
                            

                              return (
                                <HoverLinkPreview url={link}>
                                  {/* <span
                                    onClick={() => handleReadMore(policyTitle)}
                                    className="cursor-pointer text-primary hover:underline"
                                  > */}
                                    {policyTitle}
                                  {/* </span> */}
                                </HoverLinkPreview>
                              );
                            })()}
                          </dd>
                        </dl>
                        <div className="space-y-3">
                          <div>
                            <h2 className="text-xs italic leading-8 font-bold tracking-tight">
                              <span className="text-primary hover:text-primary-600 cursor-pointer">
                                {organization}
                              </span>
                            </h2>
                            <div
                              className="max-w-none text-foreground/50 text-ellipsis"
                              dangerouslySetInnerHTML={{
                                __html: policyDescription?.slice(0, 90),
                              }}
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => handleReadMore(policyTitle)}
                          className="my-2 text-primary hover:text-primary-600"
                        >
                          Read more
                        </button>
                      </article>
                    </li>
                  );
                })}
              </ul>
            ) : (
              // </div>
              <div className="rounded-sm border border-stroke bg-white px-5 py-10 shadow-default dark:border-strokedark dark:bg-boxdark sm:py-20">
                <div className="mx-auto max-w-[410px]">
                  <img
                    src="/assets/images/animation/illustration.svg"
                    alt="illustration"
                  />
                  <div className="mt-7.5 text-center">
                    <h2 className="mb-3 text-sm font-bold text-foreground">
                      Sorry, the item can’t be found
                    </h2>
                    <p className="text-xs text-foreground/50 font-medium">
                      The item you were looking for appears to have been moved,
                      deleted or does not exist.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          {pagination?.hasNextPage && (
            <div className="flex justify-between items-center mt-2 m-auto">
              <button
                onClick={loadMore}
                className="hover:bg-primary-600 flex-none bg-primary px-4 py-2 text-primary-foreground"
              >
                Load More
              </button>
            </div>
          )}
        </div>

        {/* {pagination && pagination.totalPages > 1 && (
              <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} />
            )} */}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full relative">
            {/* Close button */}
            <button
              onClick={toggleModal}
              type="button"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
            </button>
            <PolicyForms onSuccess={toggleModal} />
          </div>
        </div>
      )}
    </>
  );
};

export default PolicyObservatory;
